import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useAuthStore } from '../store/authStore';
import { registerPushTokenOnServer } from '../api/client';

/**
 * Centralized hook for push notifications
 * Automatically registers/unregisters tokens when users log in/out
 * Supports all user types: contractor, employee, admin
 */
export function usePushNotifications() {
  const { jwt, authUser } = useAuthStore();
  const listenersRegisteredRef = useRef(false);
  const currentTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // Only run on native platforms
    const isNative = Capacitor.isNativePlatform();
    if (!isNative) {
      return;
    }

    // Only register if user is authenticated
    if (!jwt || !authUser) {
      // User logged out - cleanup
      if (currentTokenRef.current) {
        currentTokenRef.current = null;
      }
      return;
    }

    const devLog = (...args: unknown[]) => {
      if ((import.meta as any).env?.DEV) console.log('[PushNotifications]', ...args);
    };

    const setupPush = async () => {
      try {
        devLog('Starting setup...');

        // Request permissions
        const permissionStatus = await PushNotifications.requestPermissions();
        devLog('Permission status:', permissionStatus);

        if (permissionStatus.receive !== 'granted') {
          devLog('Permission not granted');
          return;
        }

        // Register listeners only once
        if (!listenersRegisteredRef.current) {
          listenersRegisteredRef.current = true;
          devLog('Registering listeners...');

          // Handle successful registration
          PushNotifications.addListener('registration', async (token) => {
            devLog('Registration event fired, token (short):', token.value.substring(0, 20) + '...');
            currentTokenRef.current = token.value;

            const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
            devLog('Registering token on server, platform:', platform);
            
            const result = await registerPushTokenOnServer(token.value, platform);
            
            if (!result.success) {
              if (result.error === 'לא מורשה') {
                console.warn('[PushNotifications] Token registration unauthorized. Triggering logout.');
                useAuthStore.getState().logout();
              } else {
                console.error('[PushNotifications] Failed to register token on server:', result.error);
              }
            } else {
              devLog('Token registered on server successfully');
            }
          });

          // Handle registration errors
          PushNotifications.addListener('registrationError', (error) => {
            console.error('[PushNotifications] Registration error:', error);
          });

          // Handle push notifications received while app is in foreground
          PushNotifications.addListener('pushNotificationReceived', (notification) => {
            devLog('Push notification received:', notification);
          });

          // Handle push notifications tapped/opened
          PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            devLog('Push notification action performed:', notification);
          });
        }

        // Register for push notifications
        devLog('Calling PushNotifications.register()...');
        await PushNotifications.register();

        devLog('register() completed, waiting for registration event...');
        
        // Fallback: Listen for FCM token from native side via Capacitor App events
        // This is triggered by AppDelegate when FCM token is received
        const handleFCMToken = async (event: any) => {
          const token = event.data || event.detail || event;
          if (token && typeof token === 'string' && !currentTokenRef.current) {
            devLog('Received FCM token via fallback (short):', token.substring(0, 20) + '...');
            currentTokenRef.current = token;

            const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
            devLog('Registering FCM token on server, platform:', platform);

            const result = await registerPushTokenOnServer(token, platform);

            if (!result.success) {
              if (result.error === 'לא מורשה') {
                console.warn('[PushNotifications] FCM token registration unauthorized. Triggering logout.');
                useAuthStore.getState().logout();
              } else {
                console.error('[PushNotifications] Failed to register FCM token on server:', result.error);
              }
            } else {
              devLog('FCM token registered on server successfully');
            }
          }
        };
        
        // Listen for FCM token event from native via window events
        // This is triggered by AppDelegate when FCM token is received
        if (Capacitor.isNativePlatform()) {
          // Listen via window events
          const windowEventHandler = (event: any) => {
            let token = event.detail || event.data || event;
            // If token is JSON string, parse it
            if (typeof token === 'string' && token.startsWith('{')) {
              try {
                const parsed = JSON.parse(token);
                token = parsed.token || token;
              } catch (e) {
                // Not JSON, use as is
              }
            }
            if (token && typeof token === 'string') {
              handleFCMToken(token);
            }
          };
          
          (window as any).addEventListener('fcmTokenReceived', windowEventHandler);
          
          // Check localStorage for token (primary method since native stores it there)
          const checkLocalStorageForToken = () => {
            try {
              const storedToken = localStorage.getItem('fcmToken');
              if (storedToken && !currentTokenRef.current) {
                devLog('Found FCM token in localStorage (short):', storedToken.substring(0, 20) + '...');
                handleFCMToken(storedToken);
                // Don't clear from localStorage - keep it for future use
              }
            } catch (e) {
              console.error('[PushNotifications] Error reading localStorage:', e);
            }
          };
          
          // Check localStorage immediately
          checkLocalStorageForToken();
          
          // Check if token is already available (in case it was received before listener was set)
          setTimeout(() => {
            if (!currentTokenRef.current) {
              devLog('Registration event did not fire after 3 seconds, checking localStorage...');
              checkLocalStorageForToken();
            }
          }, 3000);
          
          // Also check periodically (every 1 second for 15 seconds)
          let checkCount = 0;
          const checkInterval = setInterval(() => {
            checkCount++;
            if (currentTokenRef.current || checkCount >= 15) {
              clearInterval(checkInterval);
              if (!currentTokenRef.current) {
                devLog('FCM token not found after 15 seconds of checking');
              }
            } else {
              checkLocalStorageForToken();
            }
          }, 1000);
        }
        
      } catch (error) {
        console.error('[PushNotifications] Error setting up push notifications:', error);
      }
    };

    // Defer on native so WKWebView first layout / input stack are not competing with permission + register work.
    let cancelled = false;
    const delayMs = Capacitor.isNativePlatform() ? 500 : 0;
    const timerId = window.setTimeout(() => {
      if (!cancelled) void setupPush();
    }, delayMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [jwt, authUser]);

  // Cleanup listeners when component unmounts (app closes)
  useEffect(() => {
    return () => {
      if (listenersRegisteredRef.current) {
        PushNotifications.removeAllListeners().catch((err) =>
          console.error('Error removing push listeners:', err)
        );
        listenersRegisteredRef.current = false;
      }
    };
  }, []);
}

